import { type KnownUserName } from '../services/users.services';

/**
 * The name fields of the "add a permitted user" row, and where their contents came from.
 *
 * <p>
 * Kept as a value with explicit transitions rather than as three loose pieces of component state,
 * because the interesting part is not any single field but the rule connecting them: a name the
 * lookup filled in belongs to the address it was fetched for, and stops being true the moment that
 * address changes. Expressed as scattered `setState` calls that rule was easy to write and easy to
 * miss — the first version of this screen missed it, and the result was that correcting a mistyped
 * address could create a brand-new person under the previous one's name.
 * </p>
 */
export interface AddRowName {
  readonly firstName: string;
  readonly lastName: string;

  /**
   * The address `firstName`/`lastName` were auto-filled for, or undefined when they were typed by
   * hand or are empty. This is what distinguishes "the administrator meant this" from "the server
   * told us this about somebody else".
   */
  readonly autoFilledFor?: string;

  /**
   * Whether the person already has a name on their profile. The fields are locked when true,
   * because the server will not overwrite a name somebody set about themselves and an editable box
   * would invite input that is silently discarded.
   */
  readonly isOnFile: boolean;
}

export const emptyAddRowName: AddRowName = {
  firstName: '',
  lastName: '',
  autoFilledFor: undefined,
  isOnFile: false,
};

/**
 * Reconciles the current contents against the address now being added.
 *
 * <p>
 * An auto-filled name survives only while its own address does. Anything typed by hand is left
 * alone — the administrator meant it, and it is not this function's business to second-guess them.
 * </p>
 */
export const forAddress = (state: AddRowName, email: string): AddRowName => {
  if (state.autoFilledFor == null || state.autoFilledFor === email) {
    return state;
  }

  return emptyAddRowName;
};

/**
 * Applies a lookup answer for `email`.
 *
 * <p>
 * A name comes back only when the caller's own company already knows the address. When it does the
 * fields are filled and locked; when it does not, whatever the administrator has typed stands,
 * because "we do not know them" is not a reason to discard someone's typing.
 * </p>
 */
export const withLookup = (state: AddRowName, email: string, known: KnownUserName | undefined): AddRowName => {
  const firstName = known?.firstName ?? '';
  const lastName = known?.lastName ?? '';
  const hasName = firstName !== '' || lastName !== '';

  if (!hasName) {
    return { ...state, isOnFile: false };
  }

  return { firstName, lastName, autoFilledFor: email, isOnFile: true };
};

/** The administrator typing in one of the fields. Their input is theirs, so the marker is dropped. */
export const withTypedFirstName = (state: AddRowName, firstName: string): AddRowName => ({
  ...state,
  firstName,
  autoFilledFor: undefined,
});

/** @see withTypedFirstName */
export const withTypedLastName = (state: AddRowName, lastName: string): AddRowName => ({
  ...state,
  lastName,
  autoFilledFor: undefined,
});
